import React, {
  useContext, useEffect, useState, useRef, memo
} from 'react';
import proptypes from 'prop-types';
const { string, number } = proptypes;
import classNames from 'classnames';
import {
  Button, IconButton, TextField, Typography, Input
} from '@one-thd/sui-atomic-components';
import { AddTo } from '@thd-olt-component-react/add-to';
import { Search } from '@one-thd/sui-icons';
import { DeleteButton } from '../delete-button/DeleteButton';
import {
  QueryProvider, useDataModel, extend,
  arrayOf,
  number as numberType,
  string as stringType,
  shape as shapeType,
  customType, params
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { ListRecs } from '../list-recs/ListRecs';
import { ProductListContext } from '../ProductListContext';
import { MoreMenu } from '../more-menu/MoreMenu';
import { QuantityStepper } from '../quantity-stepper/QuantityStepper';
import {
  debounce,
  INTENT_SEARCH_EVENT,
  INTENT_EDIT_NAME_EVENT,
  cleanIntent
} from '../productListHelper';

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.groupId === nextProps.groupId
    && prevProps.intentId === nextProps.intentId
    && prevProps.intent === nextProps.intent
    && prevProps.quantity === nextProps.quantity
    && prevProps.itemOrder === nextProps.itemOrder
    && prevProps.storeId === nextProps.storeId
  );
};

export const IntentPod = memo(({
  groupId, intentId, intent, quantity, itemOrder, storeId
}) => {
  const { membershipInformation } = useStore({ varnish: true });
  const [searchKeyword, setSearchKeyword] = useState(intent);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [tempQty, setTempQty] = useState(null);

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const loyaltyMembershipInput = membershipInformation?.data?.loyaltyMembership || null;

  const {
    list, resolveItem, removeIntentItem, onEditSuccess, isSharedList, isReplaceMode, executeReplace
  } = useContext(ProductListContext);

  const onDeleteItem = () => {
    if (removeIntentItem && intentId) {
      removeIntentItem({ groupId, intentId });
    }
  };

  const [listOperations, listOperationsResponse] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache',
    lazy: true
  });

  // Update intent text
  const updateIntentText = async (newIntentText) => {
    if (!newIntentText || newIntentText.trim() === '' || newIntentText === intent) {
      return; // Don't update if empty or unchanged
    }

    try {
      // Handle ungrouped items - convert frontend ID back to null for backend
      // Don't know if this is a scenario we have
      const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

      // Simple mutation call - only update this single intent item
      await listOperations({
        variables: {
          operationName: 'UpdateItems',
          id: list.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              intentId,
              intent: cleanIntent(newIntentText),
              quantity,
              itemOrder: itemOrder || 1
            }]
          }]
        }
      });

      LIFE_CYCLE_EVENT_BUS.trigger(INTENT_EDIT_NAME_EVENT, {
        intent: {
          originalIntent: intent,
          updatedIntent: newIntentText,
          intentId
        },
        list: {
          id: list.id,
          name: list.name
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating intent text:', error);
    }
  };

  // Update intent quantity via backend mutation
  const updateIntentQuantity = async (newQty) => {
    if (!newQty || newQty < 1) {
      return; // Don't update if invalid
    }

    try {
      // Handle ungrouped items - convert frontend ID back to null for backend
      const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

      // Simple mutation call - only update this single intent item's quantity
      await listOperations({
        variables: {
          operationName: 'UpdateItems',
          id: list.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              intentId,
              intent: searchKeyword,
              quantity: newQty,
              itemOrder: itemOrder || 1
            }]
          }]
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating intent quantity:', error);
    }
  };

  // Handler for QuantityStepper - extracts quantity from callback
  const handleQuantityChange = ({ newQuantity }) => {
    updateIntentQuantity(newQuantity?.quantity);
  };

  // Handler for mobile quantity input field
  const handleQuantityInputChange = (event) => {
    const value = event.target.value;
    setTempQty(value === '' ? '' : parseInt(value, 10));
  };

  // Handler for mobile quantity input blur - validates and updates
  const handleQuantityInputBlur = async () => {
    let finalQty = tempQty !== null ? tempQty : quantity;

    if (finalQty === '' || finalQty < 1) {
      finalQty = 1;
    }

    setTempQty(null);

    if (finalQty !== quantity) {
      await updateIntentQuantity(finalQty);
    }
  };

  // Debounced input handler - triggers update after user stops typing
  const handleIntentChange = (event) => {
    const newValue = event.target.value;
    setSearchKeyword(newValue);

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout - triggers after user stops typing for 500ms
    const newTimeout = setTimeout(() => {
      updateIntentText(newValue);
    }, 1000);

    setTypingTimeout(newTimeout);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  // Handle listOperations response
  useEffect(() => {
    const idFromResponse = listOperationsResponse?.data?.listOperations?.id;
    const errorFromResponse = listOperationsResponse?.error;

    if (!listOperationsResponse.loading && (idFromResponse || errorFromResponse)) {
      if (idFromResponse) {
        // Don't refetch immediately - this can override user input
        // Just call onEditSuccess to notify parent
        onEditSuccess({});
      } else if (errorFromResponse) {
        // eslint-disable-next-line no-console
        console.error('Error updating intent:', errorFromResponse);
      }
    }
  }, [listOperationsResponse.loading, onEditSuccess]);

  const onIntentResolved = ({ itemId, quantity: changedQty }) => {
    // When in replace mode, use executeReplace instead of resolveItem
    if (isReplaceMode && executeReplace) {
      executeReplace({ newItemId: itemId });
      return;
    }

    resolveItem({
      groupId,
      intentId,
      itemId,
      quantity: changedQty || quantity,
      itemOrder: itemOrder || 1,
      intent: searchKeyword
    });
  };

  const gridContainerClasses = classNames('sui-w-full sui-grid sui-grid-cols-12', {
    'sui-gap-6': !isMobile,
    'sui-gap-2': isMobile
  });

  const textFieldContainerClasses = classNames('sui-grid sui-pt-2px', {
    'sui-col-span-8': !isMobile,
    'sui-col-span-9': isMobile
  });

  const quantityContainerClasses = classNames('sui-pt-2', {
    'sui-col-span-4': !isMobile,
    'sui-col-span-3 sui-mr-2': isMobile
  });

  const quantityInnerGridClasses = classNames('sui-pt-2px sui-grid sui-gap-1', {
    'sui-grid-cols-3': !isMobile,
    'sui-grid-cols-1': isMobile
  });

  // For shared lists, render text-only display
  if (isSharedList) {
    return (
      <div className="sui-flex sui-w-full sui-flex-col sui-overflow-hidden">
        <div className="sui-w-full sui-grid sui-grid-cols-12 sui-gap-6">
          <div className="sui-grid sui-col-span-8 sui-pt-2px">
            <div className="sui-flex sui-items-center sui-py-2">
              <Typography variant="body-base" className="sui-text-primary">
                {searchKeyword || intent || ''}
              </Typography>
            </div>
          </div>
          <div className="sui-pt-2 sui-col-span-4">
            <div className="sui-pt-2px sui-grid sui-grid-cols-3 sui-gap-1">
              <div className="sui-col-span-2 sui-pt-1px">
                <div className="sui-flex sui-items-center sui-py-2">
                  <Typography variant="body-base" className="sui-text-primary">
                    Qty: {quantity}
                  </Typography>
                </div>
              </div>
              {/* No delete button for shared lists */}
            </div>
          </div>
        </div>
        {/* No recommendations for shared lists */}
      </div>
    );
  }

  // Original editable version for non-shared lists
  return (
    <div className="sui-flex sui-w-full sui-flex-col sui-overflow-hidden">
      <div className={gridContainerClasses}>
        <div className={textFieldContainerClasses}>
          <div className="sui-flex sui-gap-4">
            <div className={classNames('sui-w-full', {
              'sui-ml-2': isMobile
            })}>
              <TextField
              fullWidth
              value={searchKeyword || ''}
              id="purchase-intent-text-field"
              onChange={handleIntentChange}
              InputProps={{
                inputProps: {
                  'data-testid': 'purchase-intent-input',
                  // style: {
                  //   paddingLeft: isMobile ? '8px' : '0'
                  // }
                },
              }}
            />
            </div>
            {!isMobile && (
              <div className="sui-pt-2">
                <QueryProvider
                  cacheKey="list-add-to-materials"
                  defaultVariables={{
                    storeId,
                    isBrandPricingPolicyCompliant: true,
                    loyaltyMembershipInput,
                  }}
                >
                  <AddTo
                    addToType="List"
                    list={{
                      listId: list.id
                    }}
                    products={[]}
                    productPodItemData={{ searchKeyword, quantity, onIntentResolved }}
                    CustomAddToActionElement={({ onClick }) => (
                      <Button
                        aria-label="intent-pod-search-button"
                        data-testid="intent-pod-search-button"
                        onClick={(event) => {
                          // Section 9.1: Intent Actions Analytics
                          LIFE_CYCLE_EVENT_BUS.trigger(INTENT_SEARCH_EVENT, {
                            intentText: searchKeyword,
                            intentId,
                            groupId
                          });
                          onClick(event);
                        }}
                        variant="tertiary"
                      >
                        <Search size="small" />
                      </Button>
                    )}
                  />
                </QueryProvider>
              </div>
            )}
          </div>
        </div>
        <div className={quantityContainerClasses}>
          <div className={quantityInnerGridClasses}>
            <div className="sui-col-span-2 sui-pt-1px">
              {isMobile ? (
                <Input
                  type="number"
                  value={tempQty !== null ? tempQty : quantity}
                  onChange={handleQuantityInputChange}
                  onBlur={handleQuantityInputBlur}
                  fullWidth
                />
              ) : (
                <QuantityStepper
                  quantity={quantity}
                  onQuantityChange={handleQuantityChange}
                />
              )}
            </div>
            <div className="sui-flex sui-flex-row sui-gap-1 sui-justify-end sui-pb-4">
              {!isMobile && (
                <>
                  <DeleteButton
                    intentId={intentId}
                    groupId={groupId}
                    iconSize="small"
                    data-testid="intent-pod-delete-button"
                  />
                  <MoreMenu
                    iconSize="small"
                    intentId={intentId}
                    intent={searchKeyword}
                    groupId={groupId}
                    quantity={quantity}
                    itemOrder={itemOrder}
                    isIntentPod
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <QueryProvider
        cacheKey="list-recs"
        defaultVariables={{
          storeId,
          isBrandPricingPolicyCompliant: true,
          loyaltyMembershipInput
        }}
      >
        <ListRecs
          quantity={quantity}
          keyword={searchKeyword}
          onIntentResolved={onIntentResolved}
        />
      </QueryProvider>
    </div>
  );
}, areEqual);

IntentPod.displayName = 'IntentPod';

IntentPod.dataModel = extend(
  {
    listOperations: params({
      operationName: customType('Operation').enum(
        ['Delete', 'RemoveItems', 'UpdateItems', 'Create', 'Update', 'AddItems'], 'Create'),
      id: stringType().isRequired(),
      groups: arrayOf(customType('GroupInput').shape({
        groupId: stringType(),
        groupName: stringType(),
        groupOrder: stringType(),
        products: arrayOf(customType('ProductInput').shape({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          intent: stringType()
        }))
      }))
    }).mutation().shape({
      id: stringType(),
      groups: arrayOf(shapeType({
        groupId: stringType(),
        status: stringType(),
        items: arrayOf(shapeType({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          status: stringType(),
          intent: stringType(),
          intentId: stringType()
        }))
      }))
    }),
  }
);

IntentPod.propTypes = {
  groupId: string,
  intentId: string.isRequired,
  intent: string.isRequired,
  quantity: number,
  itemOrder: number,
  storeId: string
};

IntentPod.defaultProps = {
  groupId: null,
  quantity: 1,
  itemOrder: 1,
  storeId: ''
};
